import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const GREEN = "#0F4C3A";
const GOLD = "#C5A265";
const INK = "#1A1C19";
const SLATE = "#64748B";

const today = () => new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });

const TIER_DEFS = [
  { name: "Leader",     range: "85 – 100%", color: "#0F4C3A", desc: "Industry-leading maturity. Capabilities are best-in-class, sustained over time and resistant to competitive erosion. Focus: protect the lead and selectively expand." },
  { name: "Advanced",   range: "70 – 84%",  color: "#1A6B53", desc: "Strong maturity with localized opportunities. Most dimensions perform above industry norms. Focus: harden the weak links and codify what works." },
  { name: "Developing", range: "55 – 69%",  color: "#C5A265", desc: "Solid foundations but uneven execution. Capabilities exist in pockets. Focus: standardize, scale and lift the trailing dimensions." },
  { name: "Emerging",   range: "40 – 54%",  color: "#B07A3A", desc: "Capabilities are nascent or inconsistent across the organization. Focus: prioritize a small number of high-leverage interventions and build management cadence." },
  { name: "At Risk",    range: "0 – 39%",   color: "#B33A3A", desc: "Significant gaps that threaten performance and continuity. Focus: immediate executive intervention; structured turnaround plan recommended." },
];

const LIK = { 1: "Strongly Disagree", 2: "Disagree", 3: "Neutral", 4: "Agree", 5: "Strongly Agree" };

function drawAppendix(pdf, { scope = "Module" } = {}) {
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 48;

  pdf.addPage();
  pdf.setFillColor(INK); pdf.rect(0, 0, W, 80, "F");
  pdf.setFillColor(GOLD); pdf.rect(0, 80, W, 3, "F");
  pdf.setTextColor(GOLD); pdf.setFontSize(8); pdf.setFont("helvetica", "bold");
  pdf.text("APPENDIX · METHODOLOGY & MATURITY DEFINITIONS", M, 36);
  pdf.setTextColor("#FFFFFF"); pdf.setFontSize(18); pdf.setFont("helvetica", "normal");
  pdf.text("Methodology", M, 64);

  let y = 120;

  // --- Methodology
  pdf.setTextColor(INK); pdf.setFontSize(11); pdf.setFont("helvetica", "bold");
  pdf.text("How this assessment is conducted", M, y); y += 18;

  pdf.setFontSize(10); pdf.setFont("helvetica", "normal"); pdf.setTextColor(SLATE);
  const methodology = [
    `This ${scope === "Index" ? "Business Growth Readiness Index" : "assessment"} draws on Worldly Solutions' enterprise maturity framework — a structured diagnostic refined across engagements spanning 21 industries and 18+ countries.`,
    "",
    `Respondents rate ${scope === "Index" ? "each module across" : ""} a set of capability dimensions on a 5-point Likert scale, where each rating reflects the organization's self-assessed maturity against industry-grade practice:`,
  ];
  methodology.forEach(line => {
    const wrapped = pdf.splitTextToSize(line, W - 2 * M);
    pdf.text(wrapped, M, y); y += wrapped.length * 14 + 2;
  });
  y += 4;

  // Likert legend
  Object.entries(LIK).forEach(([v, label]) => {
    pdf.setFillColor(v >= "4" ? GREEN : v >= "3" ? "#1A6B53" : v >= "2" ? GOLD : "#B33A3A");
    pdf.circle(M + 6, y - 3, 4, "F");
    pdf.setTextColor(INK); pdf.setFontSize(10); pdf.setFont("helvetica", "bold");
    pdf.text(`${v}`, M + 18, y);
    pdf.setFont("helvetica", "normal"); pdf.setTextColor(SLATE);
    pdf.text(`— ${label}`, M + 28, y);
    y += 16;
  });

  y += 6;
  pdf.setTextColor(INK); pdf.setFontSize(10);
  const scoring = pdf.splitTextToSize(
    `Scoring: each dimension contributes equally to the module score. The percentage shown is (sum of responses ÷ maximum possible) × 100. ${scope === "Index" ? "The Enterprise Readiness Score is the equally-weighted mean of all completed module scores." : "Industry benchmark comparisons use a 70% median anchor derived from cross-industry engagement data."}`,
    W - 2 * M
  );
  pdf.setFont("helvetica", "normal"); pdf.setTextColor(SLATE);
  pdf.text(scoring, M, y); y += scoring.length * 14 + 18;

  // --- Maturity Tier definitions
  if (y > H - 220) { pdf.addPage(); y = M; }
  pdf.setTextColor(INK); pdf.setFontSize(11); pdf.setFont("helvetica", "bold");
  pdf.text("Maturity Tier definitions", M, y); y += 18;
  pdf.setDrawColor("#E2E8F0"); pdf.line(M, y, W - M, y); y += 14;

  TIER_DEFS.forEach(t => {
    if (y > H - 80) { pdf.addPage(); y = M; }
    // color pill
    pdf.setFillColor(t.color); pdf.rect(M, y - 9, 4, 12, "F");
    pdf.setTextColor(t.color); pdf.setFontSize(11); pdf.setFont("helvetica", "bold");
    pdf.text(t.name, M + 12, y);
    pdf.setTextColor(SLATE); pdf.setFontSize(9); pdf.setFont("helvetica", "normal");
    pdf.text(t.range, W - M, y, { align: "right" });
    y += 14;
    pdf.setTextColor(INK); pdf.setFontSize(9);
    const descLines = pdf.splitTextToSize(t.desc, W - 2 * M - 12);
    pdf.text(descLines, M + 12, y);
    y += descLines.length * 12 + 10;
  });

  // Disclaimer
  if (y > H - 80) { pdf.addPage(); y = M; }
  y += 8;
  pdf.setDrawColor("#E2E8F0"); pdf.line(M, y, W - M, y); y += 14;
  pdf.setFontSize(8); pdf.setTextColor(SLATE); pdf.setFont("helvetica", "italic");
  const disc = pdf.splitTextToSize(
    "Disclaimer: This report is generated from self-reported responses and is intended as an indicative diagnostic — not a substitute for a full Worldly Solutions consulting engagement. For a confidential walkthrough, methodology deep-dive and a 90-day action plan, please contact our team.",
    W - 2 * M
  );
  pdf.text(disc, M, y);
}

function drawPageFooters(pdf) {
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 48;
  const pageCount = pdf.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8); pdf.setTextColor(SLATE); pdf.setFont("helvetica", "normal");
    pdf.text("Worldly Solutions Private Limited · operations@worldlysolutions.in · +91-8019534565", M, H - 24);
    pdf.text(`Page ${i} of ${pageCount}`, W - M, H - 24, { align: "right" });
  }
}

// --- Single module PDF ---
export async function exportAssessmentPDF({ mod, answers, score, tier, chartsEl }) {
  const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 48;

  // ---- Cover header
  pdf.setFillColor(INK); pdf.rect(0, 0, W, 130, "F");
  pdf.setFillColor(GREEN); pdf.rect(0, 130, W, 4, "F");

  pdf.setTextColor(GOLD);
  pdf.setFontSize(8); pdf.setFont("helvetica", "bold");
  pdf.text("WORLDLY SOLUTIONS · ENTERPRISE HEALTH ASSESSMENT", M, 50);

  pdf.setTextColor("#FFFFFF");
  pdf.setFontSize(24); pdf.setFont("helvetica", "normal");
  pdf.text(mod.name, M, 85);
  pdf.setFontSize(10); pdf.setTextColor("#A8B5C2");
  pdf.text(`Executive Summary · ${today()}`, M, 105);

  let y = 170;

  // ---- Headline question
  pdf.setTextColor(INK); pdf.setFontSize(14); pdf.setFont("helvetica", "bold");
  const headlineLines = pdf.splitTextToSize(mod.headline, W - 2 * M);
  pdf.text(headlineLines, M, y);
  y += headlineLines.length * 18 + 4;

  pdf.setFontSize(10); pdf.setFont("helvetica", "normal"); pdf.setTextColor(SLATE);
  const subLines = pdf.splitTextToSize(mod.subhead, W - 2 * M);
  pdf.text(subLines, M, y);
  y += subLines.length * 14 + 18;

  // ---- KPI tiles
  const tileW = (W - 2 * M - 24) / 3;
  const tileH = 110;
  const tiles = [
    { lbl: "Overall Score", val: `${score.pct}%`, sub: `${score.sum}/${score.max} pts`, bg: INK, fg: "#FFFFFF" },
    { lbl: "Maturity Tier", val: tier.tier, sub: tier.desc, bg: "#FFFFFF", fg: INK, accent: tier.color },
    { lbl: "vs. Industry", val: `${score.pct - 70 > 0 ? "+" : ""}${score.pct - 70}%`, sub: "vs. median (70%)", bg: "#FFFFFF", fg: INK },
  ];

  tiles.forEach((t, i) => {
    const x = M + i * (tileW + 12);
    if (t.bg === INK) { pdf.setFillColor(INK); pdf.rect(x, y, tileW, tileH, "F"); }
    else { pdf.setDrawColor("#E2E8F0"); pdf.setLineWidth(0.5); pdf.rect(x, y, tileW, tileH, "S"); }
    pdf.setFontSize(8); pdf.setFont("helvetica", "bold");
    pdf.setTextColor(t.bg === INK ? GOLD : SLATE);
    pdf.text(t.lbl.toUpperCase(), x + 14, y + 22);
    pdf.setFontSize(22); pdf.setFont("helvetica", "normal");
    pdf.setTextColor(t.accent || t.fg);
    pdf.text(String(t.val), x + 14, y + 52);
    pdf.setFontSize(8); pdf.setTextColor(t.bg === INK ? "#A8B5C2" : SLATE);
    const subTxt = pdf.splitTextToSize(String(t.sub), tileW - 28);
    pdf.text(subTxt, x + 14, y + 72);
  });
  y += tileH + 24;

  // ---- Charts (capture from DOM)
  if (chartsEl) {
    try {
      const canvas = await html2canvas(chartsEl, { backgroundColor: "#FFFFFF", scale: 2, logging: false, useCORS: true });
      const imgData = canvas.toDataURL("image/png");
      const imgW = W - 2 * M;
      const imgH = (canvas.height * imgW) / canvas.width;
      if (y + imgH > H - 60) { pdf.addPage(); y = M; }
      pdf.addImage(imgData, "PNG", M, y, imgW, imgH, "", "FAST");
      y += imgH + 20;
    } catch (e) { /* skip charts if capture fails */ }
  }

  // ---- Dimension table
  if (y + 200 > H - 60) { pdf.addPage(); y = M; }
  pdf.setFontSize(12); pdf.setFont("helvetica", "bold"); pdf.setTextColor(INK);
  pdf.text("Dimension Breakdown", M, y); y += 16;
  pdf.setDrawColor("#E2E8F0"); pdf.line(M, y, W - M, y); y += 14;

  pdf.setFontSize(9); pdf.setFont("helvetica", "bold"); pdf.setTextColor(SLATE);
  pdf.text("DIMENSION", M, y);
  pdf.text("RESPONSE", W - M - 100, y);
  pdf.text("SCORE", W - M - 30, y, { align: "right" });
  y += 10; pdf.line(M, y, W - M, y); y += 14;

  const LIK2 = LIK;
  pdf.setFont("helvetica", "normal");
  mod.dimensions.forEach(d => {
    if (y > H - 60) { pdf.addPage(); y = M; }
    const v = answers[d.key] || 0;
    pdf.setTextColor(INK); pdf.setFontSize(10);
    pdf.text(d.label, M, y);
    pdf.setTextColor(SLATE); pdf.setFontSize(9);
    pdf.text(LIK2[v] || "—", W - M - 100, y);
    pdf.setTextColor(v >= 4 ? GREEN : v >= 3 ? "#1A6B53" : v >= 2 ? GOLD : "#B33A3A");
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(10);
    pdf.text(`${v}/5`, W - M - 30, y, { align: "right" });
    pdf.setFont("helvetica", "normal");
    y += 18;
  });

  // ---- Appendix
  drawAppendix(pdf, { scope: "Module" });

  // ---- Footer on every page
  drawPageFooters(pdf);

  pdf.save(`Worldly-Solutions-${mod.slug}-Assessment-Report.pdf`);
}

// --- Combined Growth Readiness PDF ---
export async function exportGrowthReadinessPDF({ data, overall, tier, dashboardEl }) {
  const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 48;

  pdf.setFillColor(INK); pdf.rect(0, 0, W, 130, "F");
  pdf.setFillColor(GOLD); pdf.rect(0, 130, W, 4, "F");

  pdf.setTextColor(GOLD); pdf.setFontSize(8); pdf.setFont("helvetica", "bold");
  pdf.text("WORLDLY SOLUTIONS · BUSINESS GROWTH READINESS INDEX", M, 50);
  pdf.setTextColor("#FFFFFF"); pdf.setFontSize(24); pdf.setFont("helvetica", "normal");
  pdf.text("Enterprise Readiness Scorecard", M, 85);
  pdf.setFontSize(10); pdf.setTextColor("#A8B5C2");
  pdf.text(`Combined Executive Dashboard · ${today()}`, M, 105);

  let y = 170;
  pdf.setTextColor(INK); pdf.setFontSize(36); pdf.setFont("helvetica", "normal");
  pdf.text(`${overall}%`, M, y + 30);
  pdf.setFontSize(14); pdf.setTextColor(tier.color);
  pdf.text(tier.tier, M + 100, y + 18);
  pdf.setFontSize(10); pdf.setTextColor(SLATE);
  const desc = pdf.splitTextToSize(tier.desc, W - M - 100 - M);
  pdf.text(desc, M + 100, y + 36);
  y += 70;

  if (dashboardEl) {
    try {
      const canvas = await html2canvas(dashboardEl, { backgroundColor: "#FFFFFF", scale: 2, logging: false, useCORS: true });
      const imgData = canvas.toDataURL("image/png");
      const imgW = W - 2 * M;
      const imgH = (canvas.height * imgW) / canvas.width;
      if (y + imgH > H - 60) { pdf.addPage(); y = M; }
      pdf.addImage(imgData, "PNG", M, y, imgW, imgH, "", "FAST");
      y += imgH + 16;
    } catch (e) { /* skip */ }
  }

  if (y + 250 > H - 60) { pdf.addPage(); y = M; }
  pdf.setFontSize(12); pdf.setFont("helvetica", "bold"); pdf.setTextColor(INK);
  pdf.text("Module Scores", M, y); y += 16;
  pdf.setDrawColor("#E2E8F0"); pdf.line(M, y, W - M, y); y += 14;
  pdf.setFontSize(9); pdf.setFont("helvetica", "bold"); pdf.setTextColor(SLATE);
  pdf.text("MODULE", M, y); pdf.text("STATUS", W - M - 140, y); pdf.text("SCORE", W - M - 30, y, { align: "right" });
  y += 10; pdf.line(M, y, W - M, y); y += 14;

  pdf.setFont("helvetica", "normal");
  data.forEach(d => {
    if (y > H - 60) { pdf.addPage(); y = M; }
    pdf.setTextColor(INK); pdf.setFontSize(10);
    pdf.text(d.name, M, y);
    pdf.setTextColor(SLATE); pdf.setFontSize(9);
    pdf.text(d.completed ? "Completed" : "Not started", W - M - 140, y);
    pdf.setTextColor(d.completed ? (d.pct >= 70 ? GREEN : d.pct >= 55 ? GOLD : "#B33A3A") : SLATE);
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(10);
    pdf.text(d.completed ? `${d.pct}%` : "—", W - M - 30, y, { align: "right" });
    pdf.setFont("helvetica", "normal");
    y += 18;
  });

  // ---- Appendix
  drawAppendix(pdf, { scope: "Index" });

  // ---- Footers
  drawPageFooters(pdf);

  pdf.save(`Worldly-Solutions-Growth-Readiness-Index.pdf`);
}
